/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef } from 'react';
import { Render, FallbackImage } from 'components';
import { Globals } from 'utils';
import { UserIcon } from 'assets/icons';

type DocUploadProps = {
	label: string
	file: File | null
	isRequired?: boolean
	message1?: string
	message2?: string
	accept?: string
	showImage?: boolean
	disabled?: boolean
	onUpload: (file: File) => void
}

const DocUpload = ({ label, file, isRequired = false, message1, message2, accept, showImage = false, onUpload, disabled }: DocUploadProps) => {
	const { blob, dropAreaRef } = useDocUpload(file, showImage, onUpload, accept);

	return (
		<div className="doc-upload-component">
			<div className="doc-item">
				<p className="doc-label">
					<span>{ label }</span>
					<Render condition={isRequired}>
						<span className="required"> * Obligatorio</span>
					</Render>
				</p>
				<label className="doc-upload" ref={dropAreaRef} data-empty={String(!file)}>
					<Render condition={!!showImage}>
						<div className="photo-wrapper">
							<FallbackImage
								src={blob}
								placeholder={UserIcon}
								className="fb-img"
								placeholderClassName="fb-img-placeholder"
							/>
						</div>
					</Render>
					<div className="details">
						<p className="message-1">{ message1 }</p>

						<Render condition={!!message2}>
							<p className="message-2">{ message2 }</p>
						</Render>

						<div className={ `upload-input ${ (disabled && 'disabled') || '' }` }>
							<span title={file?.name}>{ file?.name ?? 'Seleccionar archivo' }</span>
							<input
								disabled={ disabled || false }
								type="file"
								className="hidden"
								accept={accept ?? "application/pdf,image/jpeg,image/png"}
								hidden
								onChange={e => {
									if (e.target.files?.length)
										onUpload(e.target.files[0])
								}}
							/>
						</div>

						<Render condition={!file}>
							<p className="empty">No se ha seleccionado ningún archivo</p>
						</Render>
					</div>
				</label>
			</div>
		</div>
	)
}

const useDocUpload = (file: File | string | null, showImage: boolean, onUpload: (file: File) => void, accept?: string) => {
	const dropAreaRef = useRef<HTMLLabelElement>(null);

	const blob = useMemo(() => {
		if (!file || !showImage) return null;
		if (file instanceof File) {
			return URL.createObjectURL(file);
		}
		else{
			return file;
		}
	}, [file]);

	function preventDefaults(e: any) {
		e.preventDefault();
		e.stopPropagation();
	}

	function highlight() {
		dropAreaRef.current?.classList?.add('highlight');
	}

	function unhighlight() {
		dropAreaRef.current?.classList?.remove('highlight');
	}

	function isValidFormat(file: File) {
		if (!file.type) return false;
		const validFormats = accept || 'application/pdf,image/jpeg,image/png';
		return validFormats.includes(file.type);
	}

	function handleDrop(e: any) {
		unhighlight();
		let files = e.dataTransfer.files;

		if (!files.length) return;
		if (!isValidFormat(files[0]))
			return Globals.showError('Formato de archivo no permitido');

		onUpload(files[0]);
	}

	useEffect(() => {
		if (dropAreaRef.current) {
			['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
				dropAreaRef.current?.addEventListener(eventName, preventDefaults, false);
			});

			dropAreaRef.current.addEventListener('dragenter', highlight, false);
			dropAreaRef.current.addEventListener('dragover', highlight, false);
			dropAreaRef.current.addEventListener('dragleave', unhighlight, false);
			dropAreaRef.current.addEventListener('drop', handleDrop, false);
		}

		return () => {
			if (dropAreaRef.current) {
				dropAreaRef.current.removeEventListener('dragenter', highlight, false);
				dropAreaRef.current.removeEventListener('dragover', highlight, false);
				dropAreaRef.current.removeEventListener('dragleave', unhighlight, false);
				dropAreaRef.current.removeEventListener('drop', handleDrop, false);
			}
		}
	}, []);

	return {
		blob,
		dropAreaRef,
	}
}

export default DocUpload;